import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Platform,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {useTheme} from '../hooks/useTheme';
import {useArticleNavigation} from '../hooks/useArticleNavigation';
import {Icon} from '../components/Icon';
import {ArticleCard} from '../components/ArticleCard';
import {BackToTopButton} from '../components/BackToTopButton';
import {searchArticles} from '../services/api';
import {Article} from '../types/article';
import {useDebounce} from '../hooks/useDebounce';

type RootStackParamList = {
  Home: undefined;
  Article: {article: Article; index: number; articles: Article[]};
  Search: undefined;
};

export const SearchScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const {colors, isDark} = useTheme();

  // Force status bar update when screen is focused
  useFocusEffect(
    React.useCallback(() => {
      // In light mode, we want dark icons (dark-content)
      // In dark mode, we want light icons (light-content)
      StatusBar.setBarStyle(isDark ? 'light-content' : 'dark-content');
      if (Platform.OS === 'android') {
        StatusBar.setBackgroundColor(colors.background);
      }
    }, [isDark, colors.background])
  );
  
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 1000);
  
  const [results, setResults] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const flatListRef = useRef<FlatList>(null);
  const SCROLL_THRESHOLD = 300;

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setShowBackToTop(offsetY > SCROLL_THRESHOLD);
  }, []);

  const handleBackToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({offset: 0, animated: true});
  }, []);

  const [isPreview, setIsPreview] = useState(true);
  
  // Use shared article navigation hook with DIRETTA support
  const {handleArticlePress} = useArticleNavigation(results);

  useEffect(() => {
    // Reset to preview mode when query changes
    setIsPreview(true);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      return;
    }

    const performSearch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const limit = isPreview ? 4 : 20;
        const response = await searchArticles(debouncedQuery, 1, limit);
        setResults(response.posts);
      } catch (err: any) {
        setError(err.message || 'Errore nella ricerca');
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, isPreview]);

  const handleShowAll = () => {
    setIsPreview(false);
  };

  const dynamicStyles = {
    container: {backgroundColor: colors.background},
    text: {color: colors.textPrimary},
    input: {
      backgroundColor: isDark ? '#333' : '#f0f0f0',
      color: colors.textPrimary,
      borderColor: isDark ? '#444' : '#e0e0e0',
    },
    placeholder: isDark ? '#888' : '#999',
    closeButton: {
      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    }
  };

  return (
    <SafeAreaView style={[styles.safeArea, dynamicStyles.container]}>
      <StatusBar 
        barStyle={isDark ? 'light-content' : 'dark-content'} 
        backgroundColor={colors.background}
      />
      <View style={styles.header}>
        <View style={[styles.inputContainer, dynamicStyles.input]}>
            <Icon name="magnifying-glass" size={16} color={colors.textSecondary} />
            <TextInput
            style={[styles.input, {color: colors.textPrimary}]}
            placeholder="Cerca articoli..."
            placeholderTextColor={dynamicStyles.placeholder}
            value={query}
            onChangeText={setQuery}
            autoFocus
            returnKeyType="search"
            />
            {query.length > 0 && (
            <TouchableOpacity onPress={() => setQuery('')}>
                <Text style={{color: colors.textSecondary, fontSize: 16}}>✕</Text>
            </TouchableOpacity>
            )}
        </View>
        <TouchableOpacity 
            onPress={() => navigation.goBack()} 
            style={styles.cancelButton}
        >
            <Text style={{color: colors.primary, fontSize: 16, fontWeight: '600'}}>Annulla</Text>
        </TouchableOpacity>
      </View>

      {isLoading && (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}

      {!isLoading && error && (
        <View style={styles.center}>
          <Text style={{color: colors.error}}>{error}</Text>
        </View>
      )}

      {!isLoading && !error && results.length === 0 && debouncedQuery.length > 0 && (
        <View style={styles.center}>
          <Text style={{color: colors.textSecondary}}>Nessun risultato trovato</Text>
        </View>
      )}

      <FlatList
        ref={flatListRef}
        data={results}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({item, index}) => (
          <ArticleCard
            article={item}
            onPress={() => handleArticlePress(item, index)}
          />
        )}
        ListFooterComponent={
            !isLoading && isPreview && results.length === 4 ? (
                <TouchableOpacity 
                    style={styles.showAllButton}
                    onPress={handleShowAll}
                >
                    <Text style={styles.showAllText}>Vedi tutti i risultati per "{query}"</Text>
                    <Icon name="arrow-right" size={20} color="#fff" style={{marginLeft: 8}} />
                </TouchableOpacity>
            ) : null
        }
        contentContainerStyle={styles.list}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      />
      <BackToTopButton
        visible={showBackToTop}
        onPress={handleBackToTop}
        bottomOffset={100}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  inputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 12,
    marginRight: 12,
    borderWidth: 1,
  },
  input: {
    flex: 1,
    fontSize: 16,
    height: '100%',
  },
  cancelButton: {
    padding: 4,
  },
  list: {
    paddingVertical: 10,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  showAllButton: {
    backgroundColor: '#6200EE', // Default primary, will be overridden or use theme colors from hooks if needed, but here simple
    padding: 12,
    margin: 12,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  showAllText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
});
